/**
 * One group the container consumes: its row (order handle, chip, order
 * mode, edit, remove) and, unfolded, how its drops use the lists and its
 * list queue — each card folds on its own (user, 23/10/2026: "make each
 * list group collapsible"); folded, the row says how its drops use the
 * lists. A dynamic group (its rule uses tags, names or metrics) is ranked
 * by its formula whatever the link says (user, 19/10/2026).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { effectiveListOrderMode } from '@trafficpulse/shared';

import { type ListGroup, useListGroupResolve } from '@/features/list-groups';

import type { DraftGroupRef } from '../../../drop-generator.types';
import type { ContainerEditor } from '../state/useContainerEditor';
import { GroupOrderPanel } from './GroupOrderPanel';
import { GroupPickRow } from './GroupPickRow';
import type { GroupDay } from './lists-per-day/group-days';
import { ListChoicePanel } from './lists-per-day/ListChoicePanel';
import { listModeOf, MODE_CARDS } from './list-choice/list-mode';

/** "One list" · "Every list · best 10" — the folded card's line. */
const modeSummary = (link: DraftGroupRef): string => {
  const mode = listModeOf(link);
  const title = MODE_CARDS.find((c) => c.mode === mode)?.title ?? '';
  return mode === 'one' ? title : `${title} · best ${link.listsPerDay}`;
};

interface Props {
  stored: DraftGroupRef;
  group: ListGroup | undefined;
  index: number;
  count: number;
  editor: ContainerEditor;
  formulaSource: { countryKey: string; campaignWiserId: number | null };
  open: boolean;
  formulaOpen: boolean;
  onOpen: (open: boolean) => void;
  onFormulaOpen: (open: boolean) => void;
  onEdit?: () => void;
  /** Today's drops and lists of this group (the Today grid). */
  day?: GroupDay;
  /** 21/10/2026 — Re-pick the list (absent on a container not saved yet). */
  onRepick?: () => void;
  repicking?: boolean;
}

export const LinkedGroupCard: FC<Props> = ({ stored, group, index, count, editor, formulaSource, open, formulaOpen, onOpen, onFormulaOpen, onEdit, day, onRepick, repicking }) => {
  const ref = { ...stored, orderMode: effectiveListOrderMode(stored.orderMode, group?.ruleJson) };
  const id = ref.listGroupId;
  const patch = (p: Partial<DraftGroupRef>) => editor.groupPatch(id, p);
  const resolved = useListGroupResolve(id); // this container's campaign's lists (19/10/2026) — their count wins
  const listCount = resolved.data ? resolved.data.lists.length : (group?.matchCount ?? null);

  return (
    <div className="rounded-lg border border-border/60 bg-background">
      <GroupPickRow
        group={group}
        listGroupId={id}
        orderMode={ref.orderMode}
        open={open}
        summary={modeSummary(ref)}
        isFirst={index === 0}
        isLast={index === count - 1}
        onSetOrderMode={(mode) => {
          patch({ orderMode: mode });
          if (mode !== 'INHERIT') onOpen(true);
          if (mode === 'FORMULA') onFormulaOpen(true);
        }}
        onToggle={() => onOpen(!open)}
        onMoveUp={() => editor.groupMove(index, index - 1)}
        onMoveDown={() => editor.groupMove(index, index + 1)}
        onRemove={() => editor.groupRemove(id)}
        onEdit={onEdit}
        onRepick={onRepick}
        repicking={repicking}
      />
      {open ? (
        <>
          <ListChoicePanel
            link={ref}
            groupName={group?.name ?? 'this group'}
            matchCount={listCount}
            verdictOn={editor.draft.verdict.enabled}
            editor={editor}
            day={day}
            onPatch={patch}
          />
          <GroupOrderPanel
            link={ref}
            group={{ name: group?.name ?? id, color: group?.color ?? '#64748b' }}
            editor={editor}
            formulaSource={formulaSource}
            onPatch={patch}
            formulaOpen={formulaOpen}
            onFormulaOpenChange={onFormulaOpen}
            day={day}
            oneList={listModeOf(ref) === 'one'}
          />
        </>
      ) : null}
    </div>
  );
};
